import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  Address,
  BaseDonationLead,
  ButtonActivity,
  CallDetails,
  CustomActivity,
  DonationDiscount,
  ENVIRONMENT,
  Environment,
  Fee,
  InputActivity,
  Lead,
  LeadWeekAvailabilityDto,
  PageActivity,
  Pricing,
  Quote,
  SponsorshipAlgorithm,
} from '@domains';
import { Deserialize, Serialize } from 'cerialize';
import { catchError, map, Observable, of, take } from 'rxjs';

import { BaseApiService } from '../base-api.service';
@Injectable({
  providedIn: 'root',
})
export class LeadsService extends BaseApiService<Lead> {
  constructor(
    @Inject(ENVIRONMENT) override config: Environment,
    override http: HttpClient,
  ) {
    super(config, http, 'leads', Lead, 'Lead', ['charity', 'partner'], ['charity', 'partner']);
  }

  override deserialize(data: any): Lead {
    const callDetails = new CallDetails(Deserialize(data.call_details, CallDetails));
    const fee = new Fee(Deserialize(data.fee, Fee));
    const discount = data?.discount ? new DonationDiscount(Deserialize(data.discount, DonationDiscount)) : undefined;
    const sponsorship_algorithm = data?.sponsorship_algorithm
      ? new SponsorshipAlgorithm(Deserialize(data.sponsorship_algorithm, SponsorshipAlgorithm))
      : undefined;
    const quote = data?.quote ? new Quote(Deserialize(data.quote, Quote)) : undefined;
    const res = new Lead({
      ...Deserialize(
        {
          ...data,
        },
        Lead,
      ),
      ...Deserialize(
        {
          ...data,
        },
        BaseDonationLead,
      ),
      fee,
      callDetails,
      discount,
      sponsorship_algorithm,
      quote,
    });
    if (res.charity?.logo && !res.charity?.logo.startsWith('http')) {
      res.charity.logo = this.config.urls.baseUrl + res.charity?.logo;
    }
    res.setInitialValue();
    return res;
  }

  override serialize(item: Lead) {
    const res = {
      ...Serialize(item, Lead),
      ...Serialize(
        {
          ...item,
        },
        BaseDonationLead,
      ),
    };
    delete res.pricing;
    delete res.payment;
    delete res.fee;
    delete res.charity_state;
    delete res.donor_state;
    delete res.partner_state;
    delete res.donation_id;
    delete res.discount;
    delete res.sponsorship_algorithm;
    delete res.quote;
    delete res.courier;
    delete res.available_candidates;
    BaseDonationLead.OnSerialized(item, res);
    return res;
  }

  createPaymentSetup(lead: Lead): Observable<{
    booking_fee: boolean;
    client_secret: string;
  }> {
    return this.http.post<{
      booking_fee: boolean;
      client_secret: string;
    }>(`${this.config.urls.baseUrl}/leads/${lead.id}/payment/setup`, {});
  }

  assignCharity(leadId: string): Observable<Lead> {
    return this.http.post<Lead>(`${this.config.urls.baseUrl}/leads/${leadId}/charity`, {});
  }

  createLeadActivity(
    leadId: string | undefined | null,
    type: PageActivity | ButtonActivity | InputActivity | CustomActivity,
    value?: any,
    prefix = '',
    origin?: string,
    request_id?: string,
  ): Observable<any> {
    if (!leadId || (!this.config.isDonor && !this.config.isCaptain)) {
      return of().pipe(take(1));
    }
    if (this.config.isCaptain && !prefix) {
      prefix = 'cpt_';
    } else if (this.config.isCaptain && !prefix?.includes('cpt_')) {
      prefix += 'cpt_';
    }
    if (Object.values(PageActivity).includes(type as PageActivity)) {
      return this.createLeadPageActivity(leadId, type as PageActivity, value, prefix, origin, request_id).pipe(take(1));
    } else if (Object.values(ButtonActivity).includes(type as ButtonActivity)) {
      return this.createLeadButtonActivity(leadId, type as ButtonActivity, value, prefix, origin, request_id).pipe(take(1));
    } else if (Object.values(InputActivity).includes(type as InputActivity)) {
      return this.createLeadInputActivity(leadId, type as InputActivity, value, prefix, origin, request_id).pipe(take(1));
    } else if (Object.values(CustomActivity).includes(type as CustomActivity)) {
      return this.createLeadCustomActivity(leadId, type as CustomActivity, value, prefix, origin, request_id).pipe(take(1));
    }
    return of().pipe(take(1));
  }

  private createLeadPageActivity(
    leadId: string,
    page: PageActivity,
    value?: any,
    prefix?: string,
    origin?: string,
    request_id?: string,
  ): Observable<any> {
    return this.http.post<any>(this.config.urls.baseUrl + `/leads/${leadId}/activity`, {
      page: prefix + page,
      value,
      origin,
      request_id,
    });
  }

  private createLeadButtonActivity(
    leadId: string,
    button: ButtonActivity,
    value?: any,
    prefix?: string,
    origin?: string,
    request_id?: string,
  ): Observable<any> {
    return this.http.post<any>(this.config.urls.baseUrl + `/leads/${leadId}/activity`, {
      button: prefix + button,
      value,
      origin,
      request_id,
    });
  }

  private createLeadInputActivity(
    leadId: string,
    input: InputActivity,
    value?: any,
    prefix?: string,
    origin?: string,
    request_id?: string,
  ): Observable<any> {
    return this.http.post<any>(this.config.urls.baseUrl + `/leads/${leadId}/activity`, {
      input: prefix + input,
      value,
      origin,
      request_id,
    });
  }

  private createLeadCustomActivity(
    leadId: string,
    custom: CustomActivity,
    value?: any,
    prefix?: string,
    origin?: string,
    request_id?: string,
  ): Observable<any> {
    return this.http.post<any>(this.config.urls.baseUrl + `/leads/${leadId}/activity`, {
      custom: prefix + custom,
      value,
      origin,
      request_id,
    });
  }

  getPricing(zip?: string, charityId?: string): Observable<Pricing> {
    return this.http
      .get<Pricing>(this.config.urls.baseUrl + '/leads/pricing', {
        params: {
          ...(zip ? { zip } : {}),
          ...(charityId ? { charity_id: charityId } : {}),
        },
      })
      .pipe(
        map((pricing: Pricing) => {
          return new Pricing(Deserialize(pricing, Pricing));
        }),
      );
  }

  updateCart(lead: Lead): Observable<Lead> {
    const reqData = {
      custom_item_ids: [] as number[],
      item_ids: [] as number[],
      disassembly: lead.specification.disassembly,
    };

    for (const item of lead.aggregatedItems || []) {
      for (let i = 0; i < item.count; i++) {
        if (item.item.type === 'CustomItem') {
          reqData.custom_item_ids.push(Number(item.item.id));
        } else if (item.item.type === 'Item') {
          reqData.item_ids.push(Number(item.item.id));
        }
      }
    }
    return this.http.post(`${this.config.urls.baseUrl}/leads/${lead.id}/cart`, reqData).pipe(
      map((res: any) => {
        return this.deserialize(res);
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Priority availability for a lead — `/leads/availability`.
   *
   * Supersedes `DonationsService.getAvailability`. Courier eligibility and the
   * fallback to daily partner slots are decided server-side from the lead's
   * `available_candidates` (written by `availabilityCheck`), so there is one
   * request here and no `isUberEligible` flag to pass.
   *
   * Sends the full address, not just the zip: courier providers need the
   * coordinates to find a store within range.
   */
  getAvailability(options: {
    address: Address;
    charityId?: string | null;
    partnerId?: string | null;
    leadId?: string | null;
    week?: number;
    year?: number;
  }): Observable<LeadWeekAvailabilityDto> {
    const { address } = options;
    const params: { [key: string]: string } = {
      'address[zip]': address?.zip ?? '',
    };
    if (address?.street) params['address[street]'] = address.street;
    if (address?.secondary) params['address[secondary]'] = address.secondary;
    if (address?.city) params['address[city]'] = address.city;
    if (address?.state) params['address[state]'] = address.state;
    if (address?.lat != null) params['address[lat]'] = String(address.lat);
    if (address?.lng != null) params['address[lng]'] = String(address.lng);
    if (options.charityId) params['charity_id'] = String(options.charityId);
    if (options.partnerId) params['partner_id'] = String(options.partnerId);
    if (options.leadId) params['lead_id'] = String(options.leadId);
    // `Number.isFinite`, not `!= null`: the caller derives these from a previous
    // response, and a response without them yields NaN, which would go out as the
    // literal string "NaN".
    if (Number.isFinite(options.week)) params['week'] = String(options.week);
    if (Number.isFinite(options.year)) params['year'] = String(options.year);

    return this.http
      .get<LeadWeekAvailabilityDto>(`${this.config.urls.baseUrl}/leads/availability`, { params })
      .pipe(catchError(this.handleError));
  }

  /**
   * The pickup-address submit — `/leads/:id/availability_check`.
   *
   * The server writes the address and `meta.manualAddress`, CLEARS the picked
   * date/part of day/hour slot, and recomputes the lead's courier candidates.
   * Use this instead of a plain `update` when the address changes, or
   * `getAvailability` cannot see any courier.
   */
  availabilityCheck(leadId: string, address: Address, manualAddress?: boolean): Observable<Lead> {
    return this.http
      .post(`${this.config.urls.baseUrl}/leads/${leadId}/availability_check`, {
        address: {
          street: address?.street,
          secondary: address?.secondary,
          city: address?.city,
          state: address?.state,
          zip: address?.zip,
          lat: address?.lat,
          lng: address?.lng,
        },
        ...(manualAddress !== undefined ? { manual_address: manualAddress } : {}),
      })
      .pipe(
        map((res: any) => this.deserialize(res)),
        catchError(this.handleError),
      );
  }

  /**
   * Recalculate quotes and the sponsorship discount — `/leads/:id/quote_check`.
   *
   * Replaces `update(id, { ...lead, quote_check: true })`: the endpoint takes no
   * body, so it can never write back a stale lead the way the update did.
   */
  quoteCheck(leadId: string): Observable<Lead> {
    return this.http.post(`${this.config.urls.baseUrl}/leads/${leadId}/quote_check`, {}).pipe(
      map((res: any) => this.deserialize(res)),
      catchError(this.handleError),
    );
  }
}
